import type {
  ForumHealthMetric,
  ForumHealthTrendCategorySeries,
  StateDistribution,
} from '../../types';

// Shared trend-line palette, matching AdminDashboard's category-trend chart.
export const CHART_COLORS = [
  '#043370', '#dc981b', '#059669', '#7c3aed', '#db2777',
  '#0891b2', '#ea580c', '#65a30d', '#9333ea', '#0d9488',
];

export const STATE_SEGMENTS: { key: keyof Omit<StateDistribution, 'total'>; label: string; color: string }[] = [
  { key: 'thriving', label: 'Thriving', color: 'bg-green-500' },
  { key: 'healthy', label: 'Healthy', color: 'bg-emerald-400' },
  { key: 'strained', label: 'Strained', color: 'bg-yellow-500' },
  { key: 'breaking', label: 'Breaking', color: 'bg-red-500' },
];

export const STATE_LABELS = ['Breaking', 'Strained', 'Healthy', 'Thriving'];
export const DIRECTION_LABELS = ['Slipping', 'Maintaining', 'Growing'];

// Radar scale config per metric: State is 1-4, Direction is 1-3.
export const RADAR_METRICS: Record<ForumHealthMetric, {
  labels: string[];
  max: number;
  scaleNote: string;
}> = {
  state: {
    labels: STATE_LABELS,
    max: 4,
    scaleNote: 'Scale: 1 Breaking · 2 Strained · 3 Healthy · 4 Thriving (Forum average per area, skipped areas excluded).',
  },
  direction: {
    labels: DIRECTION_LABELS,
    max: 3,
    scaleNote: 'Scale: 1 Slipping · 2 Maintaining · 3 Growing (Forum average per area, skipped areas excluded).',
  },
};

export function formatMetricAvg(value: number | null | undefined, metric: ForumHealthMetric): string {
  if (value == null) return 'No ratings';
  const { labels, max } = RADAR_METRICS[metric];
  const nearest = labels[Math.min(max - 1, Math.max(0, Math.round(value) - 1))];
  return `${value.toFixed(1)} (${nearest})`;
}

// YYYY-MM helpers for the radar month picker.
export function currentYearMonth(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export function formatYearMonth(ym: string): string {
  const [y, m] = ym.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

export function previousYearMonth(ym: string): string {
  const [y, m] = ym.split('-').map(Number);
  const d = new Date(y, m - 2, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

// Fallback axes for the empty placeholder radar when the backend returns no
// categories at all. Mirrors the canonical member list in
// backend/snapshots/utils.py / seed data ordering.
export const FALLBACK_RADAR_CATEGORIES = [
  'Intimacy with Jesus',
  'Marriage',
  'Parenting & Children',
  'Staff',
  'Sales',
  'Marketing',
  'Operations',
  'Finances',
  'Leadership',
  'Physical Health',
  'Mental Health',
];

// Shared shape between ForumHealthTrendResponse and MemberCategoryTrendResponse
// — both are "one line per category" data, just scoped to the forum average
// or to a single member's own ratings.
export type CategoryTrendShape = {
  month_labels: string[];
  categories: ForumHealthTrendCategorySeries[];
};
